import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import { styled } from '@mui/system';
import { useTheme } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import './ScheduleTaskPage.css'; // Make sure this CSS file is linked

import { baseUrl } from '../utils/config';
import addTaskImage from '../assets/images/addtask.png';

const FormContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  maxWidth: 600,
  width: '100%',
  [theme.breakpoints.down('md')]: { // Adjust padding and gap for smaller screens
    padding: theme.spacing(2),
    gap: theme.spacing(2),
  },
}));

const RightImageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
  padding: theme.spacing(3),
  [theme.breakpoints.down('md')]: {
    height: '250px',
    marginTop: theme.spacing(4),
    display: 'none', // Explicitly hide on medium and smaller screens
  },
}));

const ScheduledTaskScreen = () => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    selectedFormId: '',
    assignedTo: '',
    reviewer: '',
    approver: '',
    dueDate: null,
  });

  const [loggedInUserName, setLoggedInUserName] = useState(null);
  const [loggedInUserDesignation, setLoggedInUserDesignation] = useState(null);

  const [forms, setForms] = useState([]);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    const loadUserDataAndFetchDependencies = async () => {
      try {
        await _loadLoggedInUserData();
        await Promise.all([
          _fetchFormNames(),
          _fetchUserNames(),
        ]);
      } catch (error) {
        setErrorMessage((prev) => (prev ? prev + '\n' : '') + `Initialization failed: ${error.message}`);
        console.error("Initialization failed:", error);
      } finally {
        setIsLoading(false);
      }
    };
    loadUserDataAndFetchDependencies();
  }, []);

  const _loadLoggedInUserData = async () => {
    const userString = localStorage.getItem('user');
    if (userString) {
      try {
        const user = JSON.parse(userString);
        setLoggedInUserName(user.name);
        setLoggedInUserDesignation(user.designation);
        console.debug(`Loaded user: ${user.name} (${user.designation})`);
      } catch (e) {
        console.error("Error parsing user data from localStorage:", e);
        setErrorMessage("Failed to load user data from storage.");
      }
    } else {
      console.debug("User data not found in localStorage.");
    }
  };

  const _fetchUserNames = async () => {
    try {
      const response = await fetch(`${baseUrl}/api/users-credentials`);
      if (response.ok) {
        const data = await response.json();
        setUsers(data.map((item) => ({ _id: item._id, name: item.name })));
        console.log("Fetched Users:", data);
      } else {
        const errorData = await response.json();
        setErrorMessage((prev) => (prev ? prev + '\n' : '') + `Failed to load user names: ${response.status} - ${errorData.message || 'Unknown error'}`);
        console.error(`Failed to load user names. Status code: ${response.status}`, errorData);
      }
    } catch (error) {
      setErrorMessage((prev) => (prev ? prev + '\n' : '') + `Error connecting to server for users: ${error.message}`);
      console.error('Error fetching user names:', error);
    }
  };

  const _fetchFormNames = async () => {
    try {
      const response = await fetch(`${baseUrl}/api/form-names`);
      if (response.ok) {
        const data = await response.json();
        setForms(data.map((item) => ({ _id: item._id, formName: item.formName })));
        console.log("Fetched Forms:", data);
      } else {
        const errorData = await response.json();
        setErrorMessage((prev) => (prev ? prev + '\n' : '') + `Failed to load form names: ${response.status} - ${errorData.message || 'Unknown error'}`);
        console.error(`Failed to load form names. Status code: ${response.status}`, errorData);
      }
    } catch (error) {
      setErrorMessage((prev) => (prev ? prev + '\n' : '') + `Error connecting to server for forms: ${error.message}`);
      console.error('Error fetching form names:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleDateChange = (date) => {
    setFormData((prev) => ({
      ...prev,
      dueDate: date,
    }));
    if (validationErrors.dueDate) {
      setValidationErrors((prev) => ({ ...prev, dueDate: '' }));
    }
  };

  const validateForm = () => {
    let errors = {};
    let isValid = true;

    if (!formData.selectedFormId) {
      errors.selectedFormId = 'Please select a form';
      isValid = false;
    }
    if (!formData.assignedTo) {
      errors.assignedTo = 'Please select who to assign the task to';
      isValid = false;
    }
    if (!formData.reviewer) {
      errors.reviewer = 'Please select a reviewer';
      isValid = false;
    }
    if (!formData.approver) {
      errors.approver = 'Please select an approver';
      isValid = false;
    }
    if (!formData.dueDate) {
      errors.dueDate = 'Please select a due date';
      isValid = false;
    } else if (formData.dueDate.getTime() < new Date().setHours(0,0,0,0)) {
      errors.dueDate = 'Due date cannot be in the past';
      isValid = false;
    }

    setValidationErrors(errors);
    return isValid;
  };

  const _scheduleTask = async () => {
    if (!validateForm()) {
      _showSnackbar('Please fill in all required fields correctly.', 'error');
      return;
    }

    const selectedForm = forms.find(form => form._id === formData.selectedFormId);
    const formName = selectedForm ? selectedForm.formName : '';

    const scheduledByString = loggedInUserName && loggedInUserDesignation
      ? `${loggedInUserName} (${loggedInUserDesignation})`
      : 'Unknown User';

    console.log('Attempting to schedule task with the following data:');
    console.log('  formId:', formData.selectedFormId);
    console.log('  formName:', formName);
    console.log('  assignedTo (ID):', formData.assignedTo);
    console.log('  reviewer (ID):', formData.reviewer);
    console.log('  approver (ID):', formData.approver);
    console.log('  dueDate:', formData.dueDate?.toISOString());
    console.log('  scheduledAt:', new Date().toISOString());
    console.log('  scheduledBy:', scheduledByString);

    try {
      const response = await fetch(`${baseUrl}/api/schedule-task`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json; charset=UTF-8',
        },
        body: JSON.stringify({
          formId: formData.selectedFormId,
          formName: formName,
          assignedTo: formData.assignedTo,
          reviewer: formData.reviewer,
          approver: formData.approver,
          dueDate: formData.dueDate?.toISOString(),
          scheduledAt: new Date().toISOString(),
          scheduledBy: scheduledByString,
        }),
      });

      if (response.ok) {
        _showSnackbar('Task scheduled successfully', 'success');
        setFormData({
          selectedFormId: '',
          assignedTo: '',
          reviewer: '',
          approver: '',
          dueDate: null,
        });
        setValidationErrors({});
      } else {
        const errorData = await response.json();
        _showSnackbar(errorData.message || 'Failed to schedule task', 'error');
        console.error('Failed to schedule task:', errorData);
      }
    } catch (error) {
      console.error('Error scheduling task:', error);
      _showSnackbar('Failed to connect to the server', 'error');
    }
  };

  const _showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  // Helper function to render label and field similar to Flutter's buildRowLabelField
  const renderRowLabelField = (label, fieldComponent, fieldName) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        mb: 2,
        flexDirection: { xs: 'column', sm: 'row' }, // Stack on xs, row on sm and up
        alignItems: { xs: 'flex-start', sm: 'center' }, // Align label to start on xs, center on sm+
        width: '100%', // Ensure the box takes full width
      }}
    >
      <Typography
        variant="body1"
        sx={{
          fontWeight: 'bold',
          width: { xs: '100%', sm: '150px' }, // Full width label on xs, fixed on sm+
          flexShrink: 0,
          whiteSpace: { xs: 'normal', sm: 'nowrap' }, // Allow wrapping on xs, no wrap on sm+
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          mr: { xs: 0, sm: 2 }, // No right margin on xs, 2 on sm+
          mb: { xs: 1, sm: 0 }, // Add bottom margin on xs for spacing
          textAlign: { xs: 'left', sm: 'left' }, // Align text left
        }}
      >
        {label}:
      </Typography>
      <Box sx={{ flexGrow: 1, width: { xs: '100%', sm: 'auto' } }}> {/* Ensure field takes full width on xs */}
        {fieldComponent}
        {validationErrors[fieldName] && (
          <FormHelperText error>{validationErrors[fieldName]}</FormHelperText>
        )}
      </Box>
    </Box>
  );

  return (
    <div className="task-page-container">
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          minHeight: 'calc(100vh - 64px)',
          backgroundColor: '#f0f2f5',
          alignItems: 'center',
          justifyContent: 'center',
          gap: theme.spacing(4),
          p: { xs: 2, md: 3 }, // Overall padding for the screen, less on xs
        }}
      >
        {isLoading ? (
          <CircularProgress />
        ) : errorMessage ? (
          <Typography color="error">{errorMessage}</Typography>
        ) : (
          <>
            {/* Left Column: Form */}
            <Box
              sx={{
                flex: { xs: 'none', md: 3 },
                minWidth: { xs: '100%', md: 'auto' },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'center',
                p: { xs: 0, md: 8 }, // Removed extra padding on xs, FormContainer handles it
                maxWidth: { md: '700px' },
              }}
            >
              <Typography
                variant="h4"
                component="h1"
                gutterBottom
                sx={{
                  mb: { xs: 2, md: 4 }, // Adjust margin bottom
                  color: '#4A148C',
                  fontWeight: 'bold',
                  fontFamily: 'Poppins, sans-serif',
                  textAlign: { xs: 'center', md: 'left' }, // Center on xs, left on md+
                  width: '100%', // Take full width to center text
                }}
              >
                Schedule Task
              </Typography>

              <FormContainer elevation={3}>
                {renderRowLabelField(
                  'Select Form',
                  <FormControl fullWidth size="small" error={!!validationErrors.selectedFormId}>
                    <InputLabel>Form</InputLabel>
                    <Select
                      name="selectedFormId"
                      value={formData.selectedFormId || ''}
                      label="Form"
                      onChange={handleChange}
                      error={!!validationErrors.selectedFormId}
                      renderValue={(selectedId) => {
                        const selectedForm = forms.find((form) => form._id === selectedId);
                        return (
                          <span style={{
                            display: 'inline-block',
                            maxWidth: '100%', // Ensure it respects container width
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                            verticalAlign: 'middle',
                          }} title={selectedForm?.formName}>
                            {selectedForm?.formName}
                          </span>
                        );
                      }}
                    >

                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {forms.map((form) => (
                        <MenuItem key={form._id} value={form._id}>
                          {form.formName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>,
                  'selectedFormId'
                )}

                {renderRowLabelField(
                  'Assigned To',
                  <FormControl fullWidth size="small" error={!!validationErrors.assignedTo}>
                    <InputLabel>Assigned To</InputLabel>
                    <Select
                      name="assignedTo"
                      value={formData.assignedTo}
                      label="Assigned To"
                      onChange={handleChange}
                      error={!!validationErrors.assignedTo}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {users.map((user) => (
                        <MenuItem key={user._id} value={user._id}>
                          {user.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>,
                  'assignedTo'
                )}

                {renderRowLabelField(
                  'Due Date',
                  <DatePicker
                    value={formData.dueDate}
                    onChange={handleDateChange}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: 'small',
                        error: !!validationErrors.dueDate,
                      },
                    }}
                    disablePast={true}
                  />,
                  'dueDate'
                )}

                {renderRowLabelField(
                  'Reviewer',
                  <FormControl fullWidth size="small" error={!!validationErrors.reviewer}>
                    <InputLabel>Reviewer</InputLabel>
                    <Select
                      name="reviewer"
                      value={formData.reviewer}
                      label="Reviewer"
                      onChange={handleChange}
                      error={!!validationErrors.reviewer}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {users.map((user) => (
                        <MenuItem key={user._id} value={user._id}>
                          {user.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>,
                  'reviewer'
                )}

                {renderRowLabelField(
                  'Approver',
                  <FormControl fullWidth size="small" error={!!validationErrors.approver}>
                    <InputLabel>Approver</InputLabel>
                    <Select
                      name="approver"
                      value={formData.approver}
                      label="Approver"
                      onChange={handleChange}
                      error={!!validationErrors.approver}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {users.map((user) => (
                        <MenuItem key={user._id} value={user._id}>
                          {user.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>,
                  'approver'
                )}

                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-start' }}>
                  <Button
                    variant="contained"
                    onClick={_scheduleTask}
                    sx={{
                      padding: '14px 32px',
                      borderRadius: '10px',
                      boxShadow: '0 5px 15px rgba(74, 20, 140, 0.4)',
                      background: 'linear-gradient(to top left, #4A148C, #1A237E, #0D47A1)',
                      color: 'white',
                      fontSize: '18px',
                      textTransform: 'none',
                      '&:hover': {
                        background: 'linear-gradient(to bottom right, #0D47A1, #1A237E, #4A148C)',
                      },
                      width: { xs: '100%', sm: 'auto' }, // Full width on xs, auto on sm+
                    }}
                  >
                    Submit
                  </Button>
                </Box>
              </FormContainer>
            </Box>

            {/* Right Column: Image - Hidden on medium and smaller screens by RightImageContainer styled component */}
            <RightImageContainer sx={{ flex: { xs: 'none', md: 2 } }}>
              <Box
                component="img"
                src={addTaskImage}
                alt="Schedule Task Illustration"
                sx={{
                  maxHeight: '70vh',
                  width: 'auto',
                  objectFit: 'contain',
                }}
              />
            </RightImageContainer>
          </>
        )}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider></div>
  );
};

export default ScheduledTaskScreen;